import React, { useContext, useState } from 'react'
import styled, { css } from 'styled-components'
import Input from './Input'
import NavButton from './NavButton'
import Spacer from './Spacer'
import { phoneAuthService } from '../services/phone-auth'
import { COLOR } from '../constants/color'
import { formatPhoneNumber, validatePhoneNumber } from '../helpers/utils'
import { BadgesCtx } from '../BadgesCtx'

const Container = styled.div`
  width: fit-content;
  max-width: 100vw;
  margin: 0 auto;
  padding: 20px;
  align-items: center;
  display: flex;
  flex-direction: column;
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => (theme.retailer.default ? '#012979' : '#221F20')};
  text-align: center;
  margin-bottom: 10px;
`

const Description = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => (theme.retailer.default ? '#012979' : '#221F20')};
  text-align: center;
  margin-bottom: 20px;
`

const Warning = styled.p`
  color: ${COLOR.orange};
  font-size: 14px;
  text-align: center;
  margin: 10px 0;
`

const Success = styled.p`
  color: ${({ theme }) => theme.retailer.successColor};
  font-size: 14px;
  text-align: center;
  margin: 10px 0;
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 497px) {
    flex-direction: column-reverse;
    align-items: center;
  }
  button {
    @media (max-width: 497px) {
      margin: 0px;
      min-height: 0px;
      padding: 15px 33px;
    }
  }
`

const BackButton = styled.button`
  border-radius: 64px;
  padding: 24px 48px;
  background: transparent;
  border: 2px solid gray;
  cursor: pointer;
  margin: 30px 30px 20px 30px;
  display: flex;
  max-height: 64px;
  min-height: 72px;
  align-content: center;
  justify-content: center;
  align-items: center;
  flex-direction: row;

  color: gray;
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;

  &:hover {
    background: ${({ theme }) => (theme.retailer.default ? theme.retailer.primaryColor : '#221F20')};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ResendButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => (theme.retailer.default ? theme.retailer.primaryColor : '#221F20')};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const PhoneAuth = ({ onSuccess, onCancel, userName }) => {
  const { loadProgress } = useContext(BadgesCtx)
  const [authPhase, setAuthPhase] = useState('ENTER_PHONE') // 'ENTER_PHONE' | 'VERIFY_OTP'
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [resendDisabled, setResendDisabled] = useState(false)

  const handlePhoneSubmit = async () => {
    const cleanPhone = phoneNumber.replace(/[^\d]/g, '')

    if (!validatePhoneNumber(cleanPhone)) {
      setError('Please enter a valid 10-digit phone number')
      return
    }

    setLoading(true)
    setError('')

    try {
      await phoneAuthService.generatePin(cleanPhone)
      setSuccess('Verification code sent to your phone!')
      setAuthPhase('VERIFY_OTP')
    } catch (err) {
      console.error(err)
      setError('Failed to send verification code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleOtpSubmit = async () => {
    if (otp.length !== 6) {
      setError('Please enter a 6-digit verification code')
      return
    }

    setLoading(true)
    setError('')

    try {
      const cleanPhone = phoneNumber.replace(/[^\d]/g, '')
      const result = await phoneAuthService.validatePin(cleanPhone, otp)
      if (result.phoneVerificationToken) {
        await loadProgress()
        onSuccess()
      } else {
        setError('Invalid verification code. Please try again.')
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setResendDisabled(true)
    setError('')

    try {
      const cleanPhone = phoneNumber.replace(/[^\d]/g, '')
      await phoneAuthService.generatePin(cleanPhone)
      setSuccess('New verification code sent!')

      // Disable resend for 60 seconds
      setTimeout(() => setResendDisabled(false), 60000)
    } catch (err) {
      setError('Failed to resend code. Please try again.')
      setResendDisabled(false)
    }
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value)
    setPhoneNumber(formatted)
    setError('')
  }

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '').slice(0, 6)
    setOtp(value)
    setError('')
  }

  if (authPhase === 'ENTER_PHONE') {
    return (
      <Container className="animate__animated animate__fadeIn">
        <Title>Hi {userName}!</Title>
        <Description>
          To secure your account, please verify your phone number
        </Description>
        <Input
          type="tel"
          placeholder="(555) 123-4567"
          value={phoneNumber}
          onChange={handlePhoneChange}
          aria-label="Enter your phone number"
          maxLength={14}
          autoFocus
        />
        {error && <Warning>{error}</Warning>}
        {success && <Success>{success}</Success>}
        <Spacer height="20" />
        <ButtonWrapper>
          <BackButton
            onClick={onCancel}
            disabled={loading}
            aria-label="Go back to profile selection"
          >
            Back
          </BackButton>
          <NavButton
            onClick={handlePhoneSubmit}
            disabled={!phoneNumber || loading}
            title={loading ? 'Sending...' : 'Send Code'}
          />
        </ButtonWrapper>
      </Container>
    )
  }

  return (
    <Container className="animate__animated animate__fadeIn">
      <Title>Verify Your Phone</Title>
      <Description>Enter the 6-digit code sent to {phoneNumber}</Description>
      <Input
        type="text"
        placeholder="123456"
        value={otp}
        onChange={handleOtpChange}
        aria-label="Enter verification code"
        maxLength={6}
        autoFocus
        style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '2px' }}
      />
      {error && <Warning>{error}</Warning>}
      {success && <Success>{success}</Success>}
      <div style={{ textAlign: 'center' }}>
        <ResendButton onClick={handleResend} disabled={resendDisabled}>
          {resendDisabled ? 'Code sent' : 'Resend code'}
        </ResendButton>
      </div>
      <Spacer height="20" />
      <ButtonWrapper>
        <BackButton
          onClick={() => setAuthPhase('ENTER_PHONE')}
          disabled={loading}
          aria-label="Change phone number"
        >
          Change Number
        </BackButton>
        <NavButton
          onClick={handleOtpSubmit}
          disabled={otp.length !== 6 || loading}
          title={loading ? 'Verifying...' : 'Verify'}
        />
      </ButtonWrapper>
    </Container>
  )
}

export default PhoneAuth
